import React from 'react';
import { Gift, Calendar, ArrowRight, ExternalLink } from 'lucide-react';
import './GiveawaySection.css';

/**
 * GiveawaySection Component
 * 
 * Renders the current active giveaway on the homepage.
 * This component is "dumb" - it simply renders what it receives.
 * All business logic (which giveaway to show) is handled by the backend.
 * 
 * If no giveaway is passed (giveaway is null/undefined), this component
 * returns null and renders nothing. The homepage section simply doesn't exist.
 * 
 * @param {Object} giveaway - The active giveaway data from CMS
 */
const GiveawaySection = ({ giveaway }) => {
  // If no active giveaway, render nothing (not hidden, just doesn't exist)
  if (!giveaway) {
    return null;
  }

  // Format end date if provided
  const formatEndDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch {
      return null;
    }
  };

  const endDateFormatted = formatEndDate(giveaway.endDate);

  // Parse CTA URLs - ensure they have proper format
  const getPrimaryCtaUrl = () => {
    if (!giveaway.primaryCtaUrl) return '#';
    return giveaway.primaryCtaUrl;
  };

  const getSecondaryCtaUrl = () => {
    if (!giveaway.secondaryCtaUrl) return null;
    return giveaway.secondaryCtaUrl;
  };

  const isExternalLink = (url) => {
    if (!url) return false;
    return url.startsWith('http://') || url.startsWith('https://');
  };

  return (
    <section className="giveaway-section">
      <div className="container">
        <div className="giveaway-content">
          {/* Hero Image - Large, visual-first */}
          {giveaway.heroImage && (
            <div className="giveaway-hero-wrapper">
              <img 
                src={giveaway.heroImage} 
                alt={giveaway.title}
                className="giveaway-hero-image"
              />
              <div className="giveaway-badge">
                <Gift size={18} />
                <span>Active Giveaway</span>
              </div>
            </div>
          )}

          {/* Content Area */}
          <div className="giveaway-info">
            <div className="giveaway-header">
              <h2 className="giveaway-title">{giveaway.title}</h2>
              {endDateFormatted && (
                <div className="giveaway-deadline">
                  <Calendar size={16} />
                  <span>Ends {endDateFormatted}</span>
                </div>
              )}
            </div>

            {/* Description */}
            {giveaway.description && (
              <div 
                className="giveaway-description"
                dangerouslySetInnerHTML={{ __html: giveaway.description }}
              />
            )}

            {/* Rules / How to Enter */}
            {giveaway.rules && (
              <div className="giveaway-rules">
                <h3 className="giveaway-rules-title">How to Enter</h3>
                <div 
                  className="giveaway-rules-content"
                  dangerouslySetInnerHTML={{ __html: giveaway.rules }}
                />
              </div>
            )}

            {/* Eligibility Notes */}
            {giveaway.eligibilityNotes && (
              <p className="giveaway-eligibility">{giveaway.eligibilityNotes}</p>
            )}

            {/* CTAs */}
            <div className="giveaway-ctas">
              {giveaway.primaryCtaLabel && giveaway.primaryCtaUrl && (
                <a 
                  href={getPrimaryCtaUrl()}
                  className="giveaway-cta-primary"
                  target={isExternalLink(giveaway.primaryCtaUrl) ? "_blank" : "_self"}
                  rel={isExternalLink(giveaway.primaryCtaUrl) ? "noopener noreferrer" : undefined}
                >
                  {giveaway.primaryCtaLabel}
                  {isExternalLink(giveaway.primaryCtaUrl) ? (
                    <ExternalLink size={18} />
                  ) : (
                    <ArrowRight size={18} />
                  )}
                </a>
              )}
              
              {giveaway.secondaryCtaLabel && giveaway.secondaryCtaUrl && (
                <a 
                  href={getSecondaryCtaUrl()}
                  className="giveaway-cta-secondary"
                  target={isExternalLink(giveaway.secondaryCtaUrl) ? "_blank" : "_self"}
                  rel={isExternalLink(giveaway.secondaryCtaUrl) ? "noopener noreferrer" : undefined}
                >
                  {giveaway.secondaryCtaLabel}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GiveawaySection;
